// @flow
import React, { useRef, useState, useEffect, useCallback } from "react";
import { enableUserSelect, disableUserSelect } from "./reducers/BusinessLogic.tsx";
import { classNames } from "../../../lib/Util";

import "./ResizableHandle.scss";

type Props = {
    position?: number | string,
    direction?: "horizontal" | "vertical",
    onMove: (y: number) => void,
    onMoveStart?: () => void,
    onMoveStop?: () => void,
};

const HANDLER_WIDTH = 10;

/**
 * A draggable handle component for resizing elements horizontally or vertically.
 */
function ResizableHandle(props: Props) {
    const [is_divider_selected, setIsDividerSelected] = useState(false);
    const last_mouse_position_ref = useRef<number>(0);

    const isVertical = (): boolean => props.direction === "vertical";

    const getPointerPosition = (e: MouseEvent | TouchEvent): number =>
        isVertical()
            ? // $FlowFixMe
              (e.clientX ?? e.touches[0].clientX)
            : // $FlowFixMe
              (e.clientY ?? e.touches[0].clientY);

    const getStyles = (): Object => {
        const position = props.position ?? "auto";
        return isVertical() ? { left: position } : { top: position };
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
        disableUserSelect();
        setIsDividerSelected(true);
        last_mouse_position_ref.current = getPointerPosition(e);

        if (props.onMoveStart) {
            props.onMoveStart();
        }
    };

    const onPointerMove = useCallback(
        (e: MouseEvent | TouchEvent) => {
            if (is_divider_selected) {
                props.onMove(getPointerPosition(e) - last_mouse_position_ref.current);
                last_mouse_position_ref.current = getPointerPosition(e);
            }
        },
        [is_divider_selected, props]
    );

    const onPointerUp = useCallback(
        (e: MouseEvent | TouchEvent) => {
            if (is_divider_selected) {
                setIsDividerSelected(false);
                enableUserSelect();

                if (props.onMoveStop) {
                    props.onMoveStop();
                }
            }
        },
        [is_divider_selected, props]
    );

    useEffect(() => {
        window.addEventListener("mousemove", onPointerMove);
        window.addEventListener("touchmove", onPointerMove);
        window.addEventListener("mouseup", onPointerUp);
        window.addEventListener("touchend", onPointerUp);
        return () => {
            window.removeEventListener("mousemove", onPointerMove);
            window.removeEventListener("touchmove", onPointerMove);
            window.removeEventListener("mouseup", onPointerUp);
            window.removeEventListener("touchend", onPointerUp);
        };
    }, [onPointerMove, onPointerUp]);

    return (
        <div
            className={classNames("resizable-handle", {
                "resizable-handle--vertical": isVertical(),
            })}
            style={getStyles()}
            onMouseDown={onPointerDown}
            onTouchStart={onPointerDown}
            onMouseUp={onPointerUp}
            onTouchEnd={onPointerUp}
        >
            <i />
        </div>
    );
}

ResizableHandle.WIDTH = HANDLER_WIDTH;

export default ResizableHandle;
